import React, { useEffect, useMemo, useState } from "react";

import { useDebounce } from "@whoosh/utilities/src/hooks";

import { Box } from "../../primitive";
import { ActiveSliderBar } from "./components/ActiveSliderBar";
import { SliderBar } from "./components/SliderBar";
import { SliderKnob } from "./components/SliderKnob";
import { RangeSliderProps } from "./types";

const RangeSliderUnMemoized = ({
  currentLeftPercent,
  currentRightPercent,
  percentValuesMap,
  onChange,
}: RangeSliderProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [leftKnobPercent, setLeftKnobPercent] = useState(currentLeftPercent);
  const [rightKnobPercent, setRightKnobPercent] = useState(currentRightPercent);
  const [hasLeftFocus, setHasLeftFocus] = useState(false);
  const [hasRightFocus, setHasRightFocus] = useState(false);
  const hasFocus = hasLeftFocus || hasRightFocus;

  useEffect(() => {
    setLeftKnobPercent(currentLeftPercent);
  }, [currentLeftPercent]);
  useEffect(() => {
    setRightKnobPercent(currentRightPercent);
  }, [currentRightPercent]);

  const leftValue = useMemo(() => {
    return percentValuesMap
      ?.slice()
      .reverse()
      .find((item) => item.percentage <= leftKnobPercent);
  }, [leftKnobPercent, percentValuesMap]);

  const rightValue = useMemo(() => {
    return percentValuesMap
      ?.slice()
      .reverse()
      .find((item) => item.percentage <= rightKnobPercent);
  }, [rightKnobPercent, percentValuesMap]);

  const leftReturnValue = useMemo(() => {
    return leftValue?.value ?? leftKnobPercent.toString();
  }, [leftKnobPercent, leftValue?.value]);

  const rightReturnValue = useMemo(() => {
    return rightValue?.value ?? rightKnobPercent.toString();
  }, [rightKnobPercent, rightValue?.value]);

  const debouncedOnChange = useDebounce(onChange, 500);

  useEffect(() => {
    if (!isDragging) {
      debouncedOnChange(
        leftReturnValue,
        rightReturnValue,
        leftKnobPercent,
        rightKnobPercent
      );
    }
  }, [
    leftReturnValue,
    rightReturnValue,
    leftKnobPercent,
    rightKnobPercent,
    debouncedOnChange,
    isDragging,
  ]);

  const [sliderPixelWidth, setSliderPixelWidth] = useState(0);

  return (
    <Box
      onLayout={(event) => {
        const { width } = event.nativeEvent.layout;
        setSliderPixelWidth(width - 20);
      }}
      height={20}
      width="100%"
      position="relative"
    >
      <SliderBar />
      <ActiveSliderBar
        {...{ hasFocus, sliderPixelWidth, leftKnobPercent, rightKnobPercent }}
      />
      <SliderKnob
        sliderPixelWidth={sliderPixelWidth}
        leftBoundaryPercent={0}
        rightBoundaryPercent={rightKnobPercent}
        currentPercent={currentLeftPercent}
        setCurrentPercent={setLeftKnobPercent}
        setHasFocus={setHasLeftFocus}
        currentValueFromMap={leftValue}
        setIsDragging={setIsDragging}
      />
      <SliderKnob
        sliderPixelWidth={sliderPixelWidth}
        leftBoundaryPercent={leftKnobPercent}
        rightBoundaryPercent={100}
        currentPercent={currentRightPercent}
        setCurrentPercent={setRightKnobPercent}
        setHasFocus={setHasRightFocus}
        currentValueFromMap={rightValue}
        setIsDragging={setIsDragging}
        isRight
      />
    </Box>
  );
};

export const RangeSlider = React.memo(RangeSliderUnMemoized);
